import { useEffect, useState } from "react";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { NativeSelectField } from "@/components/ui/NativeSelect";
import { TextInput } from "@/components/ui/Field";
import { fetchOrderCoverPrintUrls, getOrderCoverTemplateSettings } from "@/lib/orders";
import {
  getDefaultPrintLocation,
  getPrintLocations,
  printByLocationAndWait,
  setDefaultPrintLocation,
  type PrintLocation,
} from "@/lib/projects";
import {
  PrintJobFeedbackError,
  formatPrintJobFeedback,
} from "@/lib/printJob";
import { toast } from "@/lib/toast";

export function CoverPrintDialog({
  open,
  orderIds,
  finalizeBeforePrint,
  onClose,
  onDone,
}: {
  open: boolean;
  orderIds: number[];
  /** Bestellung erst anlegen, dann drucken (Wizard-Flow ohne bestehende IDs). */
  finalizeBeforePrint?: () => Promise<number[]>;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [locations, setLocations] = useState<PrintLocation[]>([]);
  const [locationId, setLocationId] = useState("");
  const [copies, setCopies] = useState("1");
  const [onePerBn, setOnePerBn] = useState(true);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<string[]>([]);

  useEffect(() => {
    if (!open) return;
    void (async () => {
      const [locs, def, tpl] = await Promise.all([
        getPrintLocations(),
        getDefaultPrintLocation(),
        getOrderCoverTemplateSettings().catch(() => ({ one_per_bestellnummer: false })),
      ]);
      setLocations(Array.isArray(locs) ? locs : []);
      setOnePerBn(!!tpl.one_per_bestellnummer);
      setCopies("1");
      if (def?.default_location_id) setLocationId(String(def.default_location_id));
      else if (locs?.[0]) {
        const id = locs[0].id ?? locs[0].key ?? locs[0].value;
        if (id != null) setLocationId(String(id));
      } else {
        setLocationId("");
      }
    })();
  }, [open]);

  useEffect(() => {
    if (!open || !orderIds.length) {
      setPreview([]);
      return;
    }
    void fetchOrderCoverPrintUrls(orderIds, onePerBn)
      .then((data) => {
        const labels = (data.items || []).map(
          (it) => it.bestellnummer || `Bestellung #${it.order_id}`,
        );
        setPreview(labels);
      })
      .catch(() => setPreview([]));
  }, [open, orderIds, onePerBn]);

  async function handlePrint() {
    const loc = Number(locationId);
    const numCopies = Math.max(1, Number(copies) || 1);
    if (!loc) {
      onDone("Bitte Standort wählen.");
      return;
    }
    setBusy(true);
    const loadingId = toast.loading("Deckblätter werden gedruckt…");
    try {
      await setDefaultPrintLocation(loc);
      let ids = orderIds;
      if (!ids.length && finalizeBeforePrint) {
        try {
          ids = await finalizeBeforePrint();
        } catch (err) {
          toast.dismiss(loadingId);
          onDone(err instanceof Error ? err.message : "Bestellung fehlgeschlagen.");
          return;
        }
      }
      if (!ids.length) {
        toast.dismiss(loadingId);
        onDone("Keine Bestellung zum Drucken vorhanden.");
        return;
      }
      const data = await fetchOrderCoverPrintUrls(ids, onePerBn);
      const items = data.items || [];
      let successCount = 0;
      let warningCount = 0;
      let errorCount = 0;
      const errorMessages: string[] = [];

      for (const it of items) {
        if (!it.url) continue;
        try {
          const job = await printByLocationAndWait(loc, numCopies, it.url);
          const feedback = formatPrintJobFeedback(job);
          if (feedback.variant === "error") {
            errorCount += 1;
            errorMessages.push(feedback.message || feedback.title);
          } else if (feedback.variant === "warning") {
            warningCount += 1;
          } else {
            successCount += 1;
          }
        } catch (err) {
          errorCount += 1;
          errorMessages.push(err instanceof Error ? err.message : "Druck fehlgeschlagen.");
        }
      }

      toast.dismiss(loadingId);

      if (!items.length) {
        onDone("Keine Deckblätter.");
        onClose();
        return;
      }

      if (errorCount === 0 && warningCount === 0) {
        toast.success(
          successCount > 1
            ? `${successCount} Deckblätter erfolgreich gedruckt.`
            : "Druck erfolgreich.",
        );
        onDone(
          successCount > 1
            ? `${successCount} Deckblätter erfolgreich gedruckt.`
            : "Druck erfolgreich.",
        );
      } else if (errorCount === 0) {
        toast.show({
          variant: "warning",
          title: "Druck teilweise unklar",
          message: `${successCount} erfolgreich, ${warningCount} mit unsicherem Status.`,
        });
        onDone(`${successCount} gedruckt, ${warningCount} mit unsicherem Status.`);
      } else {
        toast.show({
          variant: "error",
          title: "Druck fehlgeschlagen",
          message: `${errorCount} fehlgeschlagen, ${successCount} erfolgreich.\n${errorMessages.slice(0, 3).join("\n")}`,
        });
        onDone(`${errorCount} fehlgeschlagen, ${successCount} erfolgreich.`);
      }
      onClose();
    } catch (err) {
      toast.dismiss(loadingId);
      if (err instanceof PrintJobFeedbackError) {
        onDone(err.feedback.message || err.feedback.title);
      } else {
        onDone(err instanceof Error ? err.message : "Druck fehlgeschlagen.");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Bestellung drucken"
      size="md"
      zIndexClass="z-[130]"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="button" disabled={busy || !locationId} loading={busy} onClick={() => void handlePrint()}>
            Drucken
          </Button>
        </>
      }
    >
      <NativeSelectField label="Standort" value={locationId} onChange={(e) => setLocationId(e.target.value)}>
        <option value="">— Standort —</option>
        {locations.map((loc, idx) => {
          const id = loc.id ?? loc.key ?? loc.value ?? idx;
          return (
            <option key={String(id)} value={String(id)}>
              {loc.name || loc.label || String(id)}
            </option>
          );
        })}
      </NativeSelectField>
      <TextInput
        label="Kopien"
        type="number"
        min={1}
        className="mt-2"
        value={copies}
        onChange={(e) => setCopies(e.target.value)}
      />
      <label className="mt-3 flex items-center gap-2 text-sm text-label">
        <input type="checkbox" checked={onePerBn} onChange={(e) => setOnePerBn(e.target.checked)} />
        Ein Deckblatt pro Bestellnummer
      </label>
      {preview.length > 0 ? (
        <ul className="mt-3 list-disc pl-5 text-xs text-label-secondary">
          {preview.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      ) : null}
    </Dialog>
  );
}
